import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'editor/src/store/hooks';
import getPlugin from 'editor/src/store/plugins/selector/getPlugin';
import getPluginIconByLevel from 'editor/src/store/plugins/selector/getPluginIconByLevel';
import { PluginName } from 'editor/src/store/plugins/types';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import IconUpload from 'editor/src/component/Icon/IconUpload';

import styles from './index.module.scss';

const ACCEPTED_FONT_TYPES = '.ttf,.otf';

interface Props {
  onFilesSelected: (files: File[]) => void;
}

function FontUploadButton({ onFilesSelected }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const fontUploadEnabled = useSelector((state) => state.fonts.fontUploadEnabled);
  const fontUploadButtonHidden = useSelector((state) => state.fonts.fontUploadButtonHidden);
  const fontUploadPlusRequired = useSelector((state) => state.fonts.fontUploadPlusRequired);
  const premiumLevel = useSelector((state) => getPlugin(state, PluginName.Fonts)?.extra?.premiumLevel);

  const handleClick = useCallback(() => {
    if (fontUploadPlusRequired && !fontUploadEnabled) {
      sendPostMessage('plugins.disabledPluginClick', {
        name: PluginName.Fonts,
      });
      return;
    }
    inputRef.current?.click();
  }, [fontUploadPlusRequired, fontUploadEnabled]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        onFilesSelected(files);
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onFilesSelected],
  );

  if (fontUploadButtonHidden) {
    return null;
  }

  if (!fontUploadEnabled && !fontUploadPlusRequired) {
    return null;
  }

  const isGated = fontUploadPlusRequired && !fontUploadEnabled;
  const PremiumIcon = isGated ? getPluginIconByLevel(premiumLevel) : null;

  return (
    <div className={styles.uploadButton}>
      <button type="button" className={styles.button} onClick={handleClick}>
        <IconUpload />
        <span>{t('txt_editor_font_upload_button')}</span>
        {PremiumIcon && <PremiumIcon />}
      </button>
      {!isGated && (
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_FONT_TYPES}
          multiple
          onChange={handleChange}
          className={styles.hiddenInput}
        />
      )}
    </div>
  );
}

export default React.memo(FontUploadButton);
